import { Sun, Wrench, Shield, TrendingUp, ArrowRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setFeaturedProducts(response.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };
    fetchProducts();
  }, []);

  return (
    <div className="min-h-screen">
      <section 
        className="relative h-[600px] md:h-[700px] flex items-center justify-center overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0F5F68 0%, #0F172A 100%)'
        }}
        data-testid="hero-section"
      >
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1720610784599-18c02b1cc9ee?crop=entropy&cs=srgb&fm=jpg&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 text-center text-white">
          <h1 className="font-heading font-bold text-5xl md:text-7xl tracking-tight mb-6" data-testid="hero-title">
            L'Énergie Solaire
            <br />
            Pour Demain
          </h1>
          <p className="text-lg md:text-xl leading-relaxed mb-8 text-white/90 max-w-2xl mx-auto" data-testid="hero-subtitle">
            Solutions complètes d'installation de panneaux solaires, pompes et produits électromécaniques pour particuliers et entreprises.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/produits"
              className="bg-accent text-accent-foreground hover:bg-accent/90 h-12 px-8 rounded-full font-medium transition-all hover:scale-105 active:scale-95 shadow-lg shadow-accent/20 inline-flex items-center justify-center"
              data-testid="hero-cta-products"
            >
              Découvrir nos produits
            </Link>
            <Link
              to="/contact"
              className="bg-white border border-white hover:bg-white/90 h-12 px-8 rounded-full font-medium transition-all inline-flex items-center justify-center text-secondary"
              data-testid="hero-cta-contact"
            >
              Demander un devis
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-semibold text-4xl md:text-5xl tracking-tight mb-4" data-testid="services-title">
              Nos Services
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground max-w-2xl mx-auto">
              Des solutions adaptées à vos besoins énergétiques
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 auto-rows-[minmax(180px,auto)]">
            <div className="md:col-span-2 md:row-span-2 bg-white/70 backdrop-blur-xl border border-white/20 shadow-sm p-8 rounded-2xl hover:bg-white transition-colors" data-testid="service-card-solar">
              <Sun className="w-12 h-12 text-accent mb-4" />
              <h3 className="font-heading font-medium text-2xl md:text-3xl tracking-normal mb-3">Panneaux Solaires</h3>
              <p className="text-base leading-relaxed text-muted-foreground mb-4">
                Installation professionnelle de panneaux photovoltaïques pour résidences et entreprises. Économisez jusqu'à 70% sur vos factures d'électricité.
              </p>
              <Link to="/services" className="inline-flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all">
                En savoir plus <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="bg-background/50 backdrop-blur-sm border border-border/50 p-6 rounded-2xl hover:bg-white transition-colors" data-testid="service-card-pumps">
              <Wrench className="w-10 h-10 text-primary mb-3" />
              <h3 className="font-heading font-medium text-xl mb-2">Pompes Électromécaniques</h3>
              <p className="text-sm text-muted-foreground">
                Systèmes de pompage haute performance
              </p>
            </div>

            <div className="bg-background/50 backdrop-blur-sm border border-border/50 p-6 rounded-2xl hover:bg-white transition-colors" data-testid="service-card-maintenance">
              <Shield className="w-10 h-10 text-primary mb-3" />
              <h3 className="font-heading font-medium text-xl mb-2">Maintenance & SAV</h3>
              <p className="text-sm text-muted-foreground">
                Service après-vente et entretien garantis
              </p>
            </div>

            <div className="md:col-span-2 bg-background/50 backdrop-blur-sm border border-border/50 p-6 rounded-2xl hover:bg-white transition-colors" data-testid="service-card-consulting">
              <TrendingUp className="w-10 h-10 text-primary mb-3" />
              <h3 className="font-heading font-medium text-xl mb-2">Conseil Énergétique</h3>
              <p className="text-sm text-muted-foreground">
                Analyse personnalisée de vos besoins et recommandations sur mesure
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-semibold text-4xl md:text-5xl tracking-tight mb-4" data-testid="featured-products-title">
              Produits Phares
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground">
              Découvrez notre sélection de produits de qualité professionnelle
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {featuredProducts.map((product) => (
              <Link key={product.id} to={`/produits/${product.id}`} className="group">
                <div className="bg-white border border-border/50 rounded-xl overflow-hidden hover:shadow-xl hover:border-primary/20 transition-all duration-300">
                  <div className="aspect-square overflow-hidden bg-muted">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading font-semibold text-lg mb-2 text-secondary group-hover:text-primary transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-2xl font-bold text-primary">{product.price.toFixed(2)} DH</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/produits"
              className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-full font-medium transition-all hover:scale-105 active:scale-95 shadow-lg shadow-primary/20 inline-flex items-center justify-center"
              data-testid="view-all-products-btn"
            >
              Voir tous les produits
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-muted">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-semibold text-4xl md:text-5xl tracking-tight mb-4">Pourquoi Choisir SOLARINNOV ?</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: '10+ ans d\'expérience', desc: 'Expertise reconnue dans le secteur solaire et électromécanique' },
              { title: 'Qualité garantie', desc: 'Produits certifiés et conformes aux normes européennes' },
              { title: 'Installation professionnelle', desc: 'Équipe qualifiée et formée aux dernières technologies' },
              { title: 'SAV réactif', desc: 'Service après-vente disponible et à l\'<écoute' }
            ].map((item, idx) => (
              <div key={idx} className="flex gap-4 bg-white p-6 rounded-xl" data-testid={`why-choose-item-${idx}`}>
                <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-heading font-medium text-xl mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-primary text-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading font-semibold text-4xl md:text-5xl tracking-tight mb-6" data-testid="cta-title">
            Prêt à Passer à l'Énergie Solaire ?
          </h2>
          <p className="text-lg md:text-xl leading-relaxed mb-8 text-white/90">
            Contactez-nous dès aujourd'hui pour un devis gratuit et personnalisé
          </p>
          <Link
            to="/contact"
            className="bg-accent text-accent-foreground hover:bg-accent/90 h-12 px-8 rounded-full font-medium transition-all hover:scale-105 active:scale-95 shadow-lg inline-flex items-center justify-center"
            data-testid="cta-contact-btn"
          >
            Demander un devis gratuit
          </Link>
        </div>
      </section>
    </div>
  );
};